import React from "react";
import { Radar, Gavel, Sparkles, ShieldCheck, MessageCircle, Clock, FileWarning, WifiOff } from "lucide-react";
import "./ScopriVendidirect.css";
import { COMPANY_NAME, COMPANY_VAT, SUPPORT_EMAIL } from "@/lib/company";

const LOGO_HD = "/vendidirect_logo_nebula_hd.jpg";

const WHATSAPP_NUMBER = "393478969407";
const waLink = (msg) => `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(msg)}`;

const PAIN_POINTS = [
    { icon: Clock, title: "Tempo perso", desc: "Ore intere passate a cercare manualmente annunci di privati e aggiornare fogli Excel, invece di chiudere incarichi." },
    { icon: FileWarning, title: "Troppa carta", desc: "Contratti, moduli antiriciclaggio e firme cartacee che rallentano ogni trattativa e aumentano il rischio di errore." },
    { icon: WifiOff, title: "Poca tecnologia", desc: "Nessun radar automatico, nessuna IA: le occasioni migliori arrivano prima ai competitor più veloci di te." },
];

const SOLUTIONS = [
    {
        icon: Radar, color: "blue", pill: "Recupera tempo",
        title: "Radar Privati — il tuo radar acquisizioni, sempre attivo",
        desc: "VendiDirect scansiona il web 24 ore su 24 per trovare nuovi annunci di privati nella tua zona, prima che li notino i tuoi concorrenti. Ogni contatto arriva già pronto nel tuo CRM, con numero di telefono ed email verificati. Risultato: recuperi ore di ricerca manuale ogni settimana e aumenti il numero di incarichi acquisiti ogni mese.",
    },
    {
        icon: Gavel, color: "purple", pill: "Aumenta la fiducia",
        title: "Radar Aste — non perdere più un'occasione giudiziaria",
        desc: "Il sistema incrocia automaticamente i bandi dei tribunali italiani con la tua zona operativa e i tuoi criteri, segnalandoti solo le occasioni realmente interessanti. Zero tempo perso a spulciare portali PVP: offri ai tuoi clienti investitori opportunità che nessun altro sta seguendo, e la loro fiducia in te cresce ad ogni segnalazione.",
    },
    {
        icon: Sparkles, color: "amber", pill: "Zero errori di prezzo",
        title: "AI Valutazione — un report professionale in 60 secondi",
        desc: "Basta inserire pochi dati: l'intelligenza artificiale analizza comparativi di mercato reali e genera un report PDF pronto da presentare al proprietario, non in 3 giorni ma in 60 secondi. Massima credibilità già al primo incontro: chiudi l'incarico prima che il proprietario chiami un'altra agenzia.",
    },
    {
        icon: ShieldCheck, color: "green", pill: "Zero errori legali",
        title: "Gestione Totale — CRM, AML e Firma Grafica in un'unica piattaforma",
        desc: "Lead, appuntamenti, verifica antiriciclaggio e firma grafica elettronica, tutto sotto controllo senza carta. Ogni sottoscrizione viene inserita nel PDF e archiviata nel CRM con il relativo audit.",
    },
];

const WhatsAppCta = ({ testid, msg, children }) => (
    <a href={waLink(msg)} target="_blank" rel="noreferrer" data-testid={testid} className="svd-cta-btn">
        <MessageCircle size={18} /> {children || "PRENOTA DEMO IN CALL"}
    </a>
);

export default function ScopriVendidirect() {
    return (
        <div data-testid="scopri-vendidirect-page" className="svd-page">
            <div className="svd-glow" />
            <div className="svd-container">
                <section className="svd-hero">
                    <div className="svd-logo-badge" data-testid="svd-logo-badge">
                        <img src={LOGO_HD} alt="VendiDirect" draggable="false" />
                    </div>
                    <span className="svd-eyebrow">VendiDirect · Piattaforma Immobiliare Intelligente</span>
                    <h1 className="svd-title">Smetti di rincorrere lead.<br /><span className="svd-accent">Inizia a chiudere incarichi.</span></h1>
                    <p className="svd-subtitle">
                        L'intelligenza artificiale di VendiDirect trova gli immobili prima dei tuoi concorrenti, valuta in 60 secondi
                        e gestisce ogni pratica per te: recupera fino a 10 ore a settimana da reinvestire in nuove trattative.
                    </p>
                    <WhatsAppCta testid="svd-cta-hero" msg="Ciao! Vorrei prenotare una demo gratuita di VendiDirect." />
                    <div className="svd-cta-sub">Demo in call di 20 minuti · Nessun impegno</div>
                </section>

                <section className="svd-pain-section">
                    <h2 className="svd-section-title">Perché le agenzie tradizionali stanno perdendo terreno?</h2>
                    <div className="svd-pain-grid">
                        {PAIN_POINTS.map((p) => {
                            const Icon = p.icon;
                            return (
                                <div key={p.title} className="svd-pain-card" data-testid={`svd-pain-${p.title.toLowerCase().replace(/\s+/g, "-")}`}>
                                    <Icon size={22} className="svd-pain-icon" />
                                    <div className="svd-pain-title">{p.title}</div>
                                    <p className="svd-pain-desc">{p.desc}</p>
                                </div>
                            );
                        })}
                    </div>
                    <p className="svd-pain-transition">VendiDirect risolve ognuno di questi problemi. Ecco come, funzione per funzione.</p>
                </section>

                <section className="svd-solutions-stack">
                    {SOLUTIONS.map((s, idx) => {
                        const Icon = s.icon;
                        const slug = s.title.split("—")[0].trim().toLowerCase().replace(/\s+/g, "-");
                        return (
                            <div key={s.title} className="svd-solution-block">
                                <div className="svd-solution-card" data-testid={`svd-solution-${slug}`}>
                                    <div className={`svd-icon-chip ${s.color}`}><Icon size={28} /></div>
                                    <div className="svd-solution-body">
                                        <span className={`svd-pill ${s.color}`}>{s.pill}</span>
                                        <h3 className="svd-solution-title">{s.title}</h3>
                                        <p className="svd-solution-desc">{s.desc}</p>
                                    </div>
                                </div>
                                <div className="svd-solution-cta">
                                    <WhatsAppCta testid={`svd-cta-solution-${idx}`} msg={`Ciao! Vorrei saperne di più su "${s.title.split("—")[0].trim()}" e prenotare una demo di VendiDirect.`} />
                                </div>
                            </div>
                        );
                    })}
                </section>

                <section className="svd-closing">
                    <h2>Pronto a trasformare la tua agenzia?</h2>
                    <p>Prenota una demo gratuita di 20 minuti: ti mostriamo come VendiDirect può generare nuovi contatti e far risparmiare ore di lavoro ogni settimana.</p>
                    <WhatsAppCta testid="svd-cta-closing" msg="Ciao! Vorrei prenotare una demo gratuita di VendiDirect." />
                </section>

                <div className="svd-footer-note">© {new Date().getFullYear()} {COMPANY_NAME} · P.IVA/C.F. {COMPANY_VAT} · <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a></div>
            </div>
        </div>
    );
}
